import { i18n } from "@lingui/core";
import { Trans } from "@lingui/react";
import { validateEmail, validateSSN } from "@mobility-item-page/utilities";
import type { WarpModal } from "@warp-ds/elements/modal";
import { Alert } from "@warp-ds/elements/react/alert";
import { Modal, ModalHeader } from "@warp-ds/elements/react/modal";
import { TextField } from "@warp-ds/elements/react/textfield";
import { Button } from "@warp-ds/react";
import {
  type Dispatch,
  type Ref,
  type SetStateAction,
  type SubmitEvent,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useValidDealerItemContext } from "../../context/DealerItemProviderContext.tsx";
import { validatePhone } from "../../utils/validatePhone.ts";

enum ReserveFormState {
  Initial = "initial",
  Submitting = "submitting",
  Success = "success",
  Error = "error",
}

export type ReserveCarModalHandle = {
  open: () => void;
};

type ReserveCarModalProps = {
  ref?: Ref<ReserveCarModalHandle>;
  setIsReserved: Dispatch<SetStateAction<boolean>>;
};

export const ReserveCarModal = ({ ref, setIsReserved }: ReserveCarModalProps) => {
  const modalRef = useRef<WarpModal>(null);
  const [formState, setFormState] = useState<ReserveFormState>(ReserveFormState.Initial);
  const [formData, setFormData] = useState({
    ssn: "",
    name: "",
    email: "",
    phone: "",
  });
  const [errors, setErrors] = useState({
    name: "",
    email: "",
    phone: "",
    ssn: "",
  });
  const {
    external: { brand },
  } = useValidDealerItemContext();
  const formId = useId();

  useImperativeHandle(ref, () => ({
    open: () => {
      modalRef.current?.open();
      //TODO add some tracking
    },
  }));

  const handleChange = (e: Event) => {
    const { name, value } = e.target as HTMLInputElement;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const newErrors = {
      name: "",
      email: "",
      phone: "",
      ssn: "",
    };

    if (!formData.name.trim()) {
      newErrors.name = i18n.t({ id: "dealer.reserveForm.errors.name.required", message: "Fill in your name" });
    }

    if (!validateEmail(formData.email)) {
      newErrors.email = i18n.t({
        id: "dealer.reserveForm.errors.email.invalid",
        message: "Please enter a valid email address.",
      });
    }

    if (!validatePhone(formData.phone, brand)) {
      newErrors.phone = i18n.t({
        id: "dealer.reserveForm.errors.phone.invalid",
        message: "Please enter a valid phone number.",
      });
    }

    if (!validateSSN(formData.ssn, brand)) {
      newErrors.ssn = i18n.t({
        id: "dealer.reserveForm.errors.ssn.invalid",
        message: "Please enter a valid social security number.",
      });
    }

    setErrors(newErrors);
    return Object.values(newErrors).every((error) => error === "");
  };

  const resetForm = () => {
    setFormData({
      ssn: "",
      name: "",
      email: "",
      phone: "",
    });
    setErrors({
      name: "",
      email: "",
      phone: "",
      ssn: "",
    });
    setFormState(ReserveFormState.Initial);
  };

  const handleSubmit = async (e: SubmitEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setFormState(ReserveFormState.Submitting);

    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 2000));

      setFormState(ReserveFormState.Success);
      setIsReserved(true);
    } catch (error) {
      setFormState(ReserveFormState.Error);
    }
  };

  return (
    <Modal ref={modalRef}>
      <ModalHeader slot="header" title={i18n.t({ id: "dealer.reserveForm.title", message: "Reserve this car" })} />
      <div slot="content">
        {formState !== ReserveFormState.Success ? (
          <form id={formId} onSubmit={handleSubmit}>
            <Alert variant="info" show className="mb-20">
              <h2 className="mb-4 t4 font-semibold">
                <Trans id="dealer.reserveForm.info.title" message="This is how easy it is" />
              </h2>
              <ul className="list-disc pl-16">
                <li>
                  <Trans
                    id="dealer.reserveForm.info.bullets.one"
                    message="We use electronic ID to verify you."
                    comment="Electronic ID should reflect the eID provider for each country (eg BankID in NO/SV and MitID in DA)"
                  />
                </li>
                <li>
                  <Trans
                    id="dealer.reserveForm.info.bullets.two"
                    message="You don't pay anything until you pick up the car."
                  />
                </li>
                <li>
                  <Trans
                    id="dealer.reserveForm.info.bullets.three"
                    message="The dealer will call you within 24 hours..."
                  />
                </li>
              </ul>
            </Alert>
            <h2 className="mb-4 t4 font-semibold">
              <Trans id="dealer.reserveForm.form.title" message="Your details" />
            </h2>
            <TextField
              className="block mb-16"
              label={i18n.t({ id: "dealer.reserveForm.form.name.label", message: "Your first and last name" })}
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder={i18n.t({ id: "dealer.reserveForm.form.name.placeholder", message: "Your name" })}
              invalid={!!errors.name}
              helpText={errors.name}
              disabled={formState === ReserveFormState.Submitting}
            />
            <TextField
              className="block mb-16"
              label={i18n.t({ id: "dealer.reserveForm.form.email.label", message: "Email address" })}
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={i18n.t({ id: "dealer.reserveForm.form.email.placeholder", message: "Your email address" })}
              invalid={!!errors.email}
              helpText={errors.email}
              disabled={formState === ReserveFormState.Submitting}
            />
            <TextField
              className="block mb-16"
              label={i18n.t({ id: "dealer.reserveForm.form.phone.label", message: "Phone number" })}
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder={i18n.t({ id: "dealer.reserveForm.form.phone.placeholder", message: "Your phone number" })}
              invalid={!!errors.phone}
              helpText={errors.phone}
              disabled={formState === ReserveFormState.Submitting}
            />
            <TextField
              className="block mb-16"
              label={i18n.t({ id: "dealer.reserveForm.form.ssn.label", message: "Social security number" })}
              name="ssn"
              value={formData.ssn}
              onChange={handleChange}
              placeholder={i18n.t({
                id: "dealer.reserveForm.form.ssn.placeholder",
                message: "ÅÅÅÅMMDD-XXXX",
                comment:
                  "Placeholder for social security number. Format should reflect the format for each country (eg ÅÅÅÅMMDD-XXXX in SE, DDMMYYXXXX in DK and DDMMYYXXXXX in NO)",
              })}
              invalid={!!errors.ssn}
              helpText={errors.ssn}
              disabled={formState === ReserveFormState.Submitting}
            />
            <Alert show={formState === ReserveFormState.Error} variant="negative" className="mb-24">
              <Trans
                id="dealer.reserveForm.error.text"
                message="Your reservation could not be sent. Please try again later."
              />
            </Alert>
            <Button
              primary
              type="submit"
              loading={formState === ReserveFormState.Submitting}
              className="w-full max-w-none mb-16"
            >
              <Trans id="dealer.reserveForm.form.submitButton.label" message="Reserve using electronic ID" />
            </Button>
            <Button
              secondary
              type="button"
              onClick={() => {
                modalRef.current?.close();
                resetForm();
              }}
              disabled={formState === ReserveFormState.Submitting}
              className="w-full max-w-none mb-24"
            >
              <Trans id="dealer.reserveForm.form.cancelButton.label" message="Cancel" />
            </Button>
          </form>
        ) : (
          <div className="flex flex-col">
            <Trans
              id="dealer.reserveForm.success.text"
              message="Your reservation has been sent. The dealer will contact you as soon as possible."
              comment="Message shown when reserve form is sent successfully"
            />
          </div>
        )}
      </div>
    </Modal>
  );
};
